import { map, flatMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DataService, ApiService, InspectionPluginService, UpgradesService } from '../../shared/services/index';
import { InspectionPlugin } from '../../shared/models/index';
import { InspectionFactoryService } from '../../shared/services/inspectionFactory.service';
import { ModalComponent } from '../../modal/modal.component';
import { UpgradeDetailsModalComponent } from './upgrade-details/upgrade-details.component';
import { ConfigService } from '../../shared/services/config.service';

export interface UpgradeDetailsConfig {
  key: string;
  label: string;
  valueKey: string;
  isValueArray: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UpgradesPluginService extends InspectionPluginService {
  upgradeModal: ModalComponent;
  detailsConfig: UpgradeDetailsConfig[];
  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    factory: InspectionFactoryService,
    private configService: ConfigService,
    private upgradesService: UpgradesService
  ) {
    super(apiService, dataService, null, factory);
  }

  updateUpgrades(
    inspectionId: string,
    pluginId: string,
    upgrades: any,
    headers: any = {},
    resetToDraft = true
  ): Observable<any> {
    return this._update(inspectionId, pluginId, upgrades, headers, resetToDraft).pipe(
      map((res: InspectionPlugin) => {
        return this.dataService.updatePlugin(inspectionId, res, resetToDraft);
      })
    );
  }

  getUpgrades(inspectionId: string, headers: { [header: string]: string | string[] } = {}) {
    if (this.isInspectionInOffline(inspectionId)) {
      return of([]);
    }
    return this.upgradesService.getUpgrades(inspectionId, headers).pipe(
      map((data) => {
        const upgrades: any[] = data.map((d) => ({ ...d })).filter((u) => u.type === 'Upgrade');

        const set = new Set();
        const uniqueUpgrades = upgrades.filter((u) => {
          const duplicate = set.has(u.name);
          set.add(u.name);
          return !duplicate;
        });
        uniqueUpgrades.forEach((o) => {
          o.Name = o.name;
          o.Status = o.equiped = o.equiped?.toLowerCase()?.replace(' ', '-');
          o.Benefits = [];
          this.addBenefits('availability', o);
          this.addBenefits('ergonomics', o);
          this.addBenefits('productivity', o);
          this.addBenefits('quality', o);
          this.addBenefits('safety', o);
          this.addBenefits('sustainability', o);
          o.type = o.type?.toLowerCase();
          o.downTimeImpact = o.downTimeImpact?.toLowerCase();
        });
        return uniqueUpgrades;
      })
    );
  }

  setUpgradeModal(upgradeModal: ModalComponent) {
    this.upgradeModal = upgradeModal;
    this.upgradeModal.hasClassModal = true;
    this.upgradeModal.modalFooter = false;
  }

  openUpgradeDetails(upgrade: any, config: UpgradeDetailsConfig[]) {
    this.upgradeModal.open(UpgradeDetailsModalComponent, { upgrade, config });
  }

  getUpgradesDetailsConfig(inspectionId): Observable<UpgradeDetailsConfig[]> {
    const key = 'plugins/upgrades/details-modal.json';
    if (this.detailsConfig) {
      return of(this.detailsConfig);
    }
    if (this.isInspectionInOffline(inspectionId) || !this.isConnection) {
      return of(JSON.parse(localStorage.getItem(key)));
    }
    return this.configService.getResource(key).pipe(
      tap((data) => {
        localStorage.setItem(key, JSON.stringify(data));
        this.detailsConfig = data;
      })
    );
  }

  private _update(
    inspectionId: string,
    pluginId: string,
    upgrades: any,
    headers: any = {},
    resetToDraft = true
  ): Observable<any> {
    return this.factory.updatePluginConfig(inspectionId, null, pluginId, upgrades, 'Upgrades').pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(`${plugin.postServerUrl}?resetToDraft=${resetToDraft}`, plugin.config, headers);
      })
    );
  }

  private addBenefits(benefit: string, object: any) {
    if (object[benefit]) object.Benefits.push(benefit);
  }
}
